import { type PromoCodeDeletePostBody } from "@repo/common";
import { useSnackbarController } from "@firecms/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { normalizeError } from "lib/error.utils";
import { queryKeys } from "lib/query-keys";
import { deletePromoCode as deletePromoCodeService } from "services/promo-codes/promoCodes";

export interface UsePromoCodeDeleteData {
  deletePromoCode: (formData: PromoCodeDeletePostBody) => Promise<void>;
  loading: boolean;
  error?: Error;
}

export const usePromoCodeDelete = (onDeleted?: () => void): UsePromoCodeDeleteData => {
  const snackbar = useSnackbarController();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending, error } = useMutation({
    mutationFn: deletePromoCodeService,
    onSuccess: (_response, formData) => {
      snackbar.open({
        type: "success",
        message:
          formData.documentIds.length === 1
            ? "Code erfolgreich gelöscht!"
            : `${String(formData.documentIds.length)} Codes erfolgreich gelöscht!`,
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.promoCodes.all });
      onDeleted?.();
    },
    onError: () => {
      snackbar.open({
        type: "error",
        message: "Fehler beim Löschen der Promo Codes!",
      });
    },
  });

  const deletePromoCode = useCallback(
    async (formData: PromoCodeDeletePostBody): Promise<void> => {
      await mutateAsync(formData);
    },
    [mutateAsync],
  );

  const normalizedError = error ? normalizeError(error) : undefined;

  return {
    deletePromoCode,
    loading: isPending,
    error: normalizedError,
  };
};
